import { useEffect, useMemo, useState } from "react";
import { addDoc, collection, deleteDoc, doc, getDocs, serverTimestamp } from "firebase/firestore";
import { db } from "@/firebase";

type Shikaku = { id: string; ShikakuName?: string };
type CompetitorCompany = { id: string; name?: string };
type CompetitorProduct = { id: string; name?: string; companyId?: string; isActive?: boolean };
type Mapping = { id: string; shikakuId?: string; competitorProductId?: string; isActive?: boolean };

export default function CompetitiveMappingPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const [shikakuList, setShikakuList] = useState<Shikaku[]>([]);
  const [companies, setCompanies] = useState<CompetitorCompany[]>([]);
  const [products, setProducts] = useState<CompetitorProduct[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([]);

  const [selectedShikakuId, setSelectedShikakuId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");

  const companyMap = useMemo(
    () => companies.reduce<Record<string, string>>((acc, curr) => ({ ...acc, [curr.id]: curr.name || curr.id }), {}),
    [companies],
  );

  const fetchAll = async () => {
    setLoading(true);
    setMessage("");
    try {
      const [s, c, p, m] = await Promise.all([
        getDocs(collection(db, "Shikaku")),
        getDocs(collection(db, "CompetitorCompanies")),
        getDocs(collection(db, "CompetitorProducts")),
        getDocs(collection(db, "ShikakuCompetitorMap")),
      ]);

      const shikakuRows = s.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Shikaku, "id">) }));
      const companyRows = c.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CompetitorCompany, "id">) }));
      const productRows = p.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CompetitorProduct, "id">) }));
      const mappingRows = m.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Mapping, "id">) }));

      shikakuRows.sort((a, b) => (a.ShikakuName || "").localeCompare(b.ShikakuName || "", "ja"));
      productRows.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ja"));

      setShikakuList(shikakuRows);
      setCompanies(companyRows);
      setProducts(productRows);
      setMappings(mappingRows.filter((row) => row.isActive !== false));

      if (!selectedShikakuId && shikakuRows[0]?.id) setSelectedShikakuId(shikakuRows[0].id);
    } catch (error) {
      console.error(error);
      setMessage("マッピングデータの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const mappingView = useMemo(() => {
    const byShikaku = mappings.reduce<Record<string, Mapping[]>>((acc, curr) => {
      if (!curr.shikakuId) return acc;
      if (!acc[curr.shikakuId]) acc[curr.shikakuId] = [];
      acc[curr.shikakuId].push(curr);
      return acc;
    }, {});

    return shikakuList.map((shikaku) => {
      const rows = byShikaku[shikaku.id] || [];
      const competitors = rows
        .map((row) => {
          if (!row.competitorProductId) return null;
          const product = products.find((item) => item.id === row.competitorProductId);
          if (!product) return null;
          return {
            mappingId: row.id,
            label: `${companyMap[product.companyId || ""] || "-"} / ${product.name || product.id}`,
          };
        })
        .filter((v): v is { mappingId: string; label: string } => Boolean(v));

      return {
        shikakuId: shikaku.id,
        shikakuName: shikaku.ShikakuName || "(名称未設定)",
        competitors,
      };
    });
  }, [companyMap, mappings, products, shikakuList]);

  const handleAddMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShikakuId || !selectedProductId) {
      setMessage("資格と競合商品を選択してください。");
      return;
    }

    const alreadyExists = mappings.some(
      (row) => row.shikakuId === selectedShikakuId && row.competitorProductId === selectedProductId,
    );
    if (alreadyExists) {
      setMessage("同じマッピングはすでに登録されています。");
      return;
    }

    setSaving(true);
    setMessage("");
    try {
      await addDoc(collection(db, "ShikakuCompetitorMap"), {
        shikakuId: selectedShikakuId,
        competitorProductId: selectedProductId,
        matchType: "manual",
        priority: 100,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setSelectedProductId("");
      setMessage("マッピングを登録しました。");
      await fetchAll();
    } catch (error) {
      console.error(error);
      setMessage("マッピング登録に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (mappingId: string) => {
    if (!window.confirm("このマッピングを削除しますか？")) return;
    try {
      await deleteDoc(doc(db, "ShikakuCompetitorMap", mappingId));
      await fetchAll();
    } catch (error) {
      console.error(error);
      setMessage("マッピング削除に失敗しました。");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">競合商品マッピング</h1>
        <p className="mt-1 text-sm text-gray-500">自社商品（Shikaku）と競合商品の対応（1:n）をここで管理します。</p>
      </div>

      <section className="rounded-xl border border-gray-100 bg-white p-4 md:p-6">
        <h2 className="text-lg font-semibold text-gray-900">マッピング追加</h2>
        <form onSubmit={handleAddMapping} className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="text-sm text-gray-700">
            自社商品（資格）
            <select
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={selectedShikakuId}
              onChange={(e) => setSelectedShikakuId(e.target.value)}
            >
              <option value="">選択してください</option>
              {shikakuList.map((shikaku) => (
                <option key={shikaku.id} value={shikaku.id}>
                  {shikaku.ShikakuName || shikaku.id}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm text-gray-700">
            競合商品
            <select
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
            >
              <option value="">選択してください</option>
              {products
                .filter((item) => item.isActive !== false)
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {`${companyMap[item.companyId || ""] || "-"} / ${item.name || item.id}`}
                  </option>
                ))}
            </select>
          </label>

          <div className="flex items-end">
            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:bg-gray-400"
            >
              {saving ? "登録中..." : "マッピング登録"}
            </button>
          </div>
        </form>
        {message ? <p className="mt-3 text-sm text-gray-600">{message}</p> : null}
      </section>

      <section className="rounded-xl border border-gray-100 bg-white p-4 md:p-6">
        <h2 className="text-lg font-semibold text-gray-900">現在のマッピング一覧</h2>
        {loading ? <p className="mt-4 text-gray-500">読み込み中...</p> : null}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="px-3 py-2">自社商品</th>
                <th className="px-3 py-2">競合商品（複数）</th>
              </tr>
            </thead>
            <tbody>
              {mappingView.map((row) => (
                <tr key={row.shikakuId} className="border-b border-gray-50 align-top">
                  <td className="px-3 py-3 font-medium text-gray-900">{row.shikakuName}</td>
                  <td className="px-3 py-3">
                    {row.competitors.length === 0 ? (
                      <span className="text-gray-400">未設定</span>
                    ) : (
                      <ul className="space-y-2">
                        {row.competitors.map((item) => (
                          <li key={item.mappingId} className="flex items-center justify-between gap-2 rounded border border-gray-100 px-3 py-2">
                            <span className="text-gray-700">{item.label}</span>
                            <button
                              type="button"
                              onClick={() => handleDelete(item.mappingId)}
                              className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                            >
                              削除
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && mappingView.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-3 py-8 text-center text-gray-500">データがありません。</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
